import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, PencilSimple, Trash, Upload, ArrowLeft, ArrowRight } from '@phosphor-icons/react';
import {
  fetchProducts as fetchAllProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  uploadImage,
  isSupportedImage,
} from '../lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { CATEGORY_NAMES, defaultSizesFor } from '../lib/categories';
import BulkImport from '../components/BulkImport';

const AdminDashboard = ({ user, authReady }) => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    description: '',
    category: 'Anime',
    image_url: '',
    image_width: null,
    image_height: null,
    images: [],
    sizes: [
      { label: 'A4', price: '69' },
      { label: 'A3', price: '79' },
    ],
    trending: false,
    limited_edition: false,
  });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadFileName, setUploadFileName] = useState('');
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    if (!authReady) return;
    if (!user) {
      navigate('/admin');
    } else {
      fetchProducts();
    }
  }, [user, authReady, navigate]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setProducts(await fetchAllProducts());
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;

    // Client-side validation (any image format; converted to WebP on upload)
    for (const file of files) {
      if (!isSupportedImage(file)) {
        toast.error(`"${file.name}" is not an image file.`);
        return;
      }
      if (file.size > 25 * 1024 * 1024) {
        toast.error(`"${file.name}": too large (${(file.size / (1024 * 1024)).toFixed(1)}MB > 25MB max).`);
        return;
      }
    }

    const uploaded = [];
    let firstMeta = null;

    try {
      setUploading(true);

      for (let i = 0; i < files.length; i += 1) {
        const file = files[i];
        setUploadFileName(`${file.name} (${i + 1}/${files.length})`);
        setUploadProgress(0);

        setUploadProgress(30);
        // eslint-disable-next-line no-await-in-loop
        const result = await uploadImage(file);
        setUploadProgress(100);
        uploaded.push(result.image_url);
        if (i === 0) {
          firstMeta = {
            width: result.width || null,
            height: result.height || null,
          };
        }
      }

      setFormData((prev) => {
        const existingImages = prev.images || [];
        const nextImages = [...existingImages, ...uploaded];
        const primaryImage = nextImages[0];
        return {
          ...prev,
          images: nextImages,
          image_url: primaryImage,
          image_width:
            existingImages.length === 0 && firstMeta ? firstMeta.width : prev.image_width,
          image_height:
            existingImages.length === 0 && firstMeta ? firstMeta.height : prev.image_height,
        };
      });
      toast.success(
        uploaded.length === 1
          ? 'Image uploaded & optimized to WebP!'
          : `${uploaded.length} images uploaded & optimized to WebP!`,
      );
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setUploadFileName('');
    }
  };

  const moveImage = (idx, dir) => {
    setFormData((prev) => {
      const imgs = [...(prev.images || [])];
      const targetIdx = idx + dir;
      if (targetIdx < 0 || targetIdx >= imgs.length) return prev;
      [imgs[idx], imgs[targetIdx]] = [imgs[targetIdx], imgs[idx]];
      return { ...prev, images: imgs, image_url: imgs[0] || '' };
    });
  };

  const removeImageAt = (idx) => {
    setFormData((prev) => {
      const imgs = (prev.images || []).filter((_, i) => i !== idx);
      return { ...prev, images: imgs, image_url: imgs[0] || '' };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate sizes
    const cleanedSizes = formData.sizes
      .filter((s) => s.label.trim() && s.price !== '' && !isNaN(parseFloat(s.price)))
      .map((s) => ({ label: s.label.trim(), price: parseFloat(s.price) }));

    if (cleanedSizes.length === 0) {
      toast.error('Add at least one size with a valid price.');
      return;
    }

    try {
      const images = formData.images && formData.images.length > 0
        ? formData.images
        : (formData.image_url ? [formData.image_url] : []);

      if (images.length === 0) {
        toast.error('Please upload at least one product image.');
        return;
      }

      const basePrice = Math.min(...cleanedSizes.map((s) => s.price));
      const payload = {
        ...formData,
        images,
        image_url: images[0],
        price: basePrice,
        sizes: cleanedSizes,
      };

      if (editingProduct) {
        await updateProduct(editingProduct.id, payload);
        toast.success('Product updated successfully!');
      } else {
        await createProduct(payload);
        toast.success('Product created successfully!');
      }

      setDialogOpen(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.message || 'Failed to save product');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    const incomingSizes =
      product.sizes && product.sizes.length > 0
        ? product.sizes.map((s) => ({ label: s.label, price: String(s.price) }))
        : [
            { label: 'A4', price: '69' },
            { label: 'A3', price: '79' },
          ];
    const incomingImages =
      product.images && product.images.length > 0
        ? product.images
        : (product.image_url ? [product.image_url] : []);
    setFormData({
      name: product.name,
      price: product.price.toString(),
      description: product.description,
      category: product.category,
      image_url: incomingImages[0] || product.image_url || '',
      image_width: product.image_width || null,
      image_height: product.image_height || null,
      images: incomingImages,
      sizes: incomingSizes,
      trending: product.trending || false,
      limited_edition: product.limited_edition || false,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      await deleteProduct(productId);
      toast.success('Product deleted successfully!');
      fetchProducts();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.message || 'Failed to delete product');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      price: '',
      description: '',
      category: 'Anime',
      image_url: '',
      image_width: null,
      image_height: null,
      images: [],
      sizes: [
        { label: 'A4', price: '69' },
        { label: 'A3', price: '79' },
      ],
      trending: false,
      limited_edition: false,
    });
    setEditingProduct(null);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    resetForm();
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="text-foreground text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-20 px-4 max-w-7xl mx-auto" data-testid="admin-dashboard">
      <div className="flex justify-between items-center mb-12">
        <h1 className="font-heading font-black text-4xl sm:text-5xl text-foreground tracking-tight">
          Admin Dashboard
        </h1>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen} modal={true}>
          <DialogTrigger asChild>
            <Button
              onClick={() => {
                resetForm();
                setDialogOpen(true);
              }}
              className="bg-white text-black hover:bg-white/90"
              data-testid="add-product-button"
            >
              <Plus size={20} weight="bold" className="mr-2" />
              Add Product
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-secondary border-border text-foreground max-w-2xl max-h-[85vh] overflow-hidden flex flex-col p-0 gap-0">
            <DialogHeader className="flex-shrink-0 p-6 pb-0">
              <DialogTitle className="font-heading font-bold text-2xl">
                {editingProduct ? 'Edit Product' : 'Add New Product'}
              </DialogTitle>
            </DialogHeader>

            <form
              onSubmit={handleSubmit}
              className="flex-1 min-h-0 overflow-y-auto overscroll-contain space-y-6 mt-6 px-6 pb-6"
              onWheel={(e) => e.stopPropagation()}
            >
              <div>
                <Label htmlFor="name" className="text-foreground">
                  Product Name
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-background border-border text-foreground"
                  required
                  data-testid="product-name-input"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-foreground">
                    Sizes & Prices <span className="text-muted-foreground text-xs font-normal">(at least one required)</span>
                  </Label>
                  <button
                    type="button"
                    onClick={() =>
                      setFormData({
                        ...formData,
                        sizes: [...formData.sizes, { label: '', price: '' }],
                      })
                    }
                    className="text-xs text-muted-foreground hover:text-foreground underline"
                    data-testid="add-size-button"
                  >
                    + Add another size
                  </button>
                </div>
                <div className="space-y-2" data-testid="sizes-editor">
                  {formData.sizes.map((size, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-2"
                      data-testid={`size-row-${idx}`}
                    >
                      <Input
                        type="text"
                        placeholder="Label (e.g. A4)"
                        value={size.label}
                        onChange={(e) => {
                          const next = [...formData.sizes];
                          next[idx] = { ...next[idx], label: e.target.value };
                          setFormData({ ...formData, sizes: next });
                        }}
                        className="bg-background border-border text-foreground flex-1"
                        data-testid={`size-label-input-${idx}`}
                      />
                      <div className="relative flex-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                          ₹
                        </span>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="Price"
                          value={size.price}
                          onChange={(e) => {
                            const next = [...formData.sizes];
                            next[idx] = { ...next[idx], price: e.target.value };
                            setFormData({ ...formData, sizes: next });
                          }}
                          className="bg-background border-border text-foreground pl-7"
                          data-testid={`size-price-input-${idx}`}
                        />
                      </div>
                      {formData.sizes.length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            const next = formData.sizes.filter((_, i) => i !== idx);
                            setFormData({ ...formData, sizes: next });
                          }}
                          className="p-2 text-muted-foreground hover:text-red-500 transition-colors"
                          aria-label={`Remove size ${size.label || idx + 1}`}
                          data-testid={`remove-size-${idx}`}
                        >
                          <Trash size={18} weight="bold" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Card price shows the lowest size price (auto-calculated).
                </p>
              </div>

              <div>
                <Label htmlFor="category" className="text-foreground">
                  Category
                </Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) =>
                    setFormData((prev) => {
                      // if the prices are still the old category's defaults, switch to the new category's defaults
                      const same = (a, b) =>
                        a.length === b.length &&
                        a.every((s, i) => s.label === b[i].label && Number(s.price) === Number(b[i].price));
                      const untouched = same(prev.sizes, defaultSizesFor(prev.category));
                      return {
                        ...prev,
                        category: value,
                        sizes: untouched
                          ? defaultSizesFor(value).map((s) => ({ label: s.label, price: String(s.price) }))
                          : prev.sizes,
                      };
                    })
                  }
                >
                  <SelectTrigger className="bg-background border-border text-foreground" data-testid="product-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-secondary border-border text-foreground">
                    {CATEGORY_NAMES.map((name) => (
                      <SelectItem key={name} value={name}>
                        {name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="description" className="text-foreground">
                  Description
                </Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="bg-background border-border text-foreground min-h-[100px]"
                  required
                  data-testid="product-description-input"
                />
              </div>

              <div>
                <Label htmlFor="image" className="text-foreground">
                  Product Images <span className="text-muted-foreground text-xs font-normal">(1st image is primary · any format: JPG, PNG, HEIC, WEBP… · auto-converted to WebP)</span>
                </Label>
                <div className="mt-2 space-y-3">
                  <Input
                    id="image"
                    type="file"
                    accept="image/*,.heic,.heif,.tif,.tiff,.jfif,.avif"
                    multiple
                    onChange={(e) => {
                      handleImageUpload(e.target.files);
                      e.target.value = '';
                    }}
                    className="bg-background border-border text-foreground file:text-foreground file:bg-muted file:border-0 file:rounded-sm file:px-3 file:py-1 file:mr-3 cursor-pointer"
                    disabled={uploading}
                    data-testid="product-image-upload"
                  />
                  <div
                    role="button"
                    tabIndex={0}
                    onDragOver={(e) => {
                      e.preventDefault();
                      if (!uploading) setDragOver(true);
                    }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDragOver(false);
                      if (!uploading) handleImageUpload(e.dataTransfer.files);
                    }}
                    onPaste={(e) => {
                      const files = Array.from(e.clipboardData?.files || []);
                      if (files.length && !uploading) {
                        e.preventDefault();
                        handleImageUpload(files);
                      }
                    }}
                    className={`rounded-sm border-2 border-dashed px-4 py-6 text-center text-sm outline-none transition-colors ${
                      dragOver ? 'border-white bg-muted text-foreground' : 'border-border text-muted-foreground'
                    } focus:border-white`}
                    data-testid="image-drop-zone"
                  >
                    <Upload size={22} className="mx-auto mb-2" />
                    <p className="text-foreground">Drag &amp; drop poster images here</p>
                    <p className="text-xs mt-1">or click here and press Ctrl+V to paste a copied image</p>
                  </div>
                  {uploading && (
                    <div className="space-y-2" data-testid="upload-progress-indicator">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground truncate max-w-[70%]">
                          Uploading {uploadFileName}…
                        </span>
                        <span className="text-foreground font-medium">{uploadProgress}%</span>
                      </div>
                      <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-white transition-all duration-200"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                    </div>
                  )}
                  {!uploading && formData.images && formData.images.length > 0 && (
                    <div className="space-y-2" data-testid="image-gallery">
                      <p className="text-xs text-muted-foreground">
                        {formData.images.length} image{formData.images.length > 1 ? 's' : ''} — use arrows to reorder. First image is shown on cards.
                      </p>
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                        {formData.images.map((src, idx) => (
                          <div
                            key={src + idx}
                            className={`relative group border rounded-sm overflow-hidden aspect-square ${
                              idx === 0 ? 'border-white ring-1 ring-white/40' : 'border-border/40'
                            }`}
                            data-testid={`admin-image-${idx}`}
                          >
                            <img src={src} alt={`Preview ${idx + 1}`} className="w-full h-full object-cover" />
                            {idx === 0 && (
                              <span className="absolute top-1 left-1 bg-white text-black text-[9px] font-bold px-1.5 py-0.5 rounded-sm">
                                PRIMARY
                              </span>
                            )}
                            <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                              <button
                                type="button"
                                onClick={() => moveImage(idx, -1)}
                                disabled={idx === 0}
                                className="w-7 h-7 flex items-center justify-center bg-white/90 hover:bg-white text-black rounded-sm disabled:opacity-30 disabled:cursor-not-allowed"
                                aria-label={`Move image ${idx + 1} up`}
                                data-testid={`move-image-up-${idx}`}
                              >
                                <ArrowLeft size={14} weight="bold" />
                              </button>
                              <button
                                type="button"
                                onClick={() => moveImage(idx, 1)}
                                disabled={idx === formData.images.length - 1}
                                className="w-7 h-7 flex items-center justify-center bg-white/90 hover:bg-white text-black rounded-sm disabled:opacity-30 disabled:cursor-not-allowed"
                                aria-label={`Move image ${idx + 1} down`}
                                data-testid={`move-image-down-${idx}`}
                              >
                                <ArrowRight size={14} weight="bold" />
                              </button>
                              <button
                                type="button"
                                onClick={() => removeImageAt(idx)}
                                className="w-7 h-7 flex items-center justify-center bg-red-600 hover:bg-red-500 text-white rounded-sm"
                                aria-label={`Remove image ${idx + 1}`}
                                data-testid={`remove-image-${idx}`}
                              >
                                <Trash size={14} weight="bold" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-6">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="trending"
                    checked={formData.trending}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, trending: checked })
                    }
                    data-testid="product-trending-checkbox"
                  />
                  <Label htmlFor="trending" className="text-foreground cursor-pointer">
                    Trending
                  </Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="limited"
                    checked={formData.limited_edition}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, limited_edition: checked })
                    }
                    data-testid="product-limited-checkbox"
                  />
                  <Label htmlFor="limited" className="text-foreground cursor-pointer">
                    Limited Edition
                  </Label>
                </div>
              </div>

              <div className="flex gap-3 pt-4 border-t border-border/40 flex-shrink-0 sticky bottom-0 bg-secondary pb-2">
                <Button
                  type="button"
                  onClick={handleDialogClose}
                  variant="outline"
                  className="flex-1 border-border text-foreground hover:bg-muted"
                  data-testid="cancel-button"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-white text-black hover:bg-white/90"
                  disabled={(formData.images.length === 0 && !formData.image_url) || uploading}
                  data-testid="save-product-button"
                >
                  {uploading ? 'Uploading image…' : (editingProduct ? 'Update Product' : 'Create Product')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Products Table */}
      <BulkImport onDone={() => fetchAllProducts().then(setProducts).catch(() => {})} />

      {products.length === 0 ? (
        <div className="text-center py-20 bg-secondary border border-border/40 rounded-sm">
          <p className="text-muted-foreground text-lg mb-4">No products yet</p>
          <p className="text-muted-foreground text-sm">Click "Add Product" to create your first poster</p>
        </div>
      ) : (
        <div className="grid gap-6" data-testid="products-list">
          {products.map((product) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-secondary border border-border/40 rounded-sm p-6"
              data-testid={`admin-product-${product.id}`}
            >
              <div className="flex gap-6">
                <div className="w-32 h-32 flex-shrink-0 bg-background border border-border/40 rounded-sm overflow-hidden">
                  <img
                    src={product.image_url}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                </div>

                <div className="flex-1">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-heading font-bold text-xl text-foreground mb-1">
                        {product.name}
                      </h3>
                      <div className="flex gap-2 items-center">
                        <span className="text-muted-foreground text-sm">{product.category}</span>
                        {product.trending && (
                          <span className="bg-white text-black text-xs px-2 py-0.5 rounded-sm font-medium">
                            TRENDING
                          </span>
                        )}
                        {product.limited_edition && (
                          <span className="bg-red-600 text-white text-xs px-2 py-0.5 rounded-sm font-medium">
                            LIMITED
                          </span>
                        )}
                      </div>
                    </div>
                    <p className="font-heading font-bold text-2xl text-foreground">₹{product.price}</p>
                  </div>

                  <p className="text-foreground/80 text-sm mb-4 line-clamp-2">
                    {product.description}
                  </p>

                  <div className="flex gap-3">
                    <Button
                      onClick={() => handleEdit(product)}
                      variant="outline"
                      size="sm"
                      className="border-border text-foreground hover:bg-muted"
                      data-testid={`edit-product-${product.id}`}
                    >
                      <PencilSimple size={16} weight="bold" className="mr-2" />
                      Edit
                    </Button>
                    <Button
                      onClick={() => handleDelete(product.id)}
                      variant="outline"
                      size="sm"
                      className="border-red-600 text-red-600 hover:bg-red-600/10"
                      data-testid={`delete-product-${product.id}`}
                    >
                      <Trash size={16} weight="bold" className="mr-2" />
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
